// wizardGenerationProgress — Alpine factory for the in-wizard generation
// completion screen (Task 11). Registered in the bundle (studio-bundle.ts) at
// module-evaluation time — same rationale as website-build-status.ts:1-9 — so
// the factory is always present before Alpine initialises the x-data element,
// eliminating the race that bites when an HX-Location boost-swap runs Alpine's
// init before an inline <script> has redefined the factory.
//
// The live-update core (onProgress / applySnapshot / isTerminal / init / poll /
// retry / stop / destroy) is the Task-11 spec (§G.1): it merges
// website_build_progress SSE snapshots (filtered by website_id, since the SSE
// bridge broadcasts to every one of the user's connections), refuses to let a
// late `running` event regress a terminal state, and self-heals via a 5s poll
// while non-terminal. The presentation getters (stAt / stProgress / grpDone /
// grpStatus / grpActiveLabel / unitLabel) are pure view helpers that the
// server-rendered checklist binds to — they never mutate state. They're
// shared with the CMS/management build-status pill via buildTimelineGetters
// (build-progress-timeline.ts); failedStageLabel below is the one
// presentation getter that stays wizard-only (it resolves a LIVE failed
// stage's precomputed label from the server-seeded stageLabels array — the
// pill's popover has no equivalent per-stage failure line).

import { buildTimelineGetters, type BuildStage } from "./build-progress-timeline";
import { boostedNavigate } from "./navigate";

type Stage = BuildStage;

export function wizardGenerationProgress({
  websiteID,
  stages,
  stageLabels,
  status,
  phase,
  siteURL,
  statusURL,
  retryURL,
  editorURL,
  landingURL,
  landed,
}: any) {
  return {
    websiteID,
    stages: (stages || []) as Stage[],
    // siteURL drives the success branch's "View website" link. Empty on the
    // first render (the screen paints while the build is still RUNNING, before
    // the deploy resolves a URL); filled reactively from the status poll's
    // `url` field on completion. _urlPolled guards the one-shot final fetch
    // below so we never loop.
    siteURL: (siteURL || "") as string,
    _urlPolled: false,
    // stageLabels is a server-translated array parallel to `stages` (index
    // i's precomputed failure-line text). A stage's key/label_args are
    // stable for its index across the run's lifetime — only its status
    // flips — so this can be computed once at render time and looked up
    // reactively when `stages` later updates. See failedStageLabel().
    stageLabels: (stageLabels || []) as string[],
    status: status as string,
    phase: phase as string,
    statusURL: statusURL as string,
    retryURL: retryURL as string,
    // editorURL (add mode only, D13): the CMS deep link of the new, hidden
    // accommodation. "" in the first-site wizard, which never navigates.
    editorURL: (editorURL || "") as string,
    // landingURL / landed / addStatus (add mode only): the run-independent
    // "landed" check — the accommodation's content file is on main — and the
    // ADD run's own status (not the website's latest run, which a superseding
    // publish can own). Phase/superseded alone never count as landed.
    landingURL: (landingURL || "") as string,
    landed: !!landed,
    addStatus: status as string,
    _landingInFlight: false,
    _timer: null as ReturnType<typeof setInterval> | null,
    _retrying: false,

    onProgress(detail: any) {
      let d: any;
      try {
        d = JSON.parse(detail.data);
      } catch {
        return;
      }
      // The SSE bridge fans every website_build_progress event out to all
      // of the user's browser connections; ignore other websites' runs.
      if (d.website_id !== this.websiteID) return;
      this.applySnapshot(d);
    },

    applySnapshot(d: any) {
      // A terminal state must not be dragged back to running by a late,
      // out-of-order event (SSE + poll can race).
      if (this.isTerminal(this.status) && d.status === "running") return;
      this.stages = d.stages || this.stages;
      this.status = d.status;
      this.phase = d.phase || this.phase;
      // The status poll carries `url` for a completed run; capture it so the
      // "View website" link appears. SSE snapshots do NOT carry it.
      if (d.url) this.siteURL = d.url;
      if (this.editorURL !== "") {
        // Add mode: the website-scoped snapshot only hints that the content
        // may have landed (deploy phase / a terminal or superseding run) —
        // ask the landing check right away instead of waiting for the tick.
        if (!this.landed && (this.phase === "deploy" || this.status !== "running")) {
          void this.checkLanding();
        }
        this.settle(this.editorReady());
        return;
      }
      if (this.isTerminal(this.status)) {
        // If the terminal state arrived via SSE (which omits `url`), fire
        // ONE more poll — the status endpoint returns the URL for a
        // completed run — then stop. Without this, an SSE-delivered
        // completion would stop polling before the link's URL ever loads.
        if (this.status === "completed" && !this.siteURL && !this._urlPolled) {
          this._urlPolled = true;
          void this.poll(); // its applySnapshot sets siteURL, then stops
          return;
        }
        this.stop();
      }
    },

    // applyLanding merges the add-mode landing check ({landed, status}).
    // landed is monotonic: content on main does not "un-land".
    applyLanding(d: any) {
      if (this.editorURL === "") return;
      const wasReady = this.editorReady();
      if (d.landed === true) this.landed = true;
      if (d.status) this.addStatus = d.status;
      this.settle(wasReady);
    },

    // settle (add mode): navigate on the live not-ready → ready transition —
    // stopping the interval FIRST so no tick fires against the swapped page —
    // and stop polling once there is nothing left to wait for.
    settle(wasReady: boolean) {
      if (!wasReady && this.editorReady()) {
        this.stop();
        boostedNavigate(this.editorURL);
        return;
      }
      if (!this.needsPolling()) this.stop();
    },

    async checkLanding() {
      // One check at a time: an SSE hint and the poll tick can coincide.
      if (this.landingURL === "" || this._landingInFlight) return;
      this._landingInFlight = true;
      try {
        const res = await fetch(this.landingURL, {
          headers: { "HX-Request": "true" },
          credentials: "same-origin",
        });
        if (res.ok) this.applyLanding(await res.json());
      } catch {
        /* transient network error — the next tick retries */
      } finally {
        this._landingInFlight = false;
      }
    },

    // D13: the editor opens once the accommodation's content file is on main
    // (whichever run committed it) and the ADD run has not failed. The
    // deploy phase or a superseding publish alone never count.
    editorReady(): boolean {
      return this.editorURL !== "" && this.landed && this.addStatus !== "failed";
    },

    // needsPolling: add mode waits for the landing (superseded counts as
    // non-terminal only while not landed); otherwise until a terminal status.
    needsPolling(): boolean {
      if (this.editorURL !== "") return !this.landed && this.addStatus !== "failed";
      return !this.isTerminal(this.status);
    },

    isTerminal(s: string): boolean {
      return s === "completed" || s === "failed";
    },

    init() {
      // Self-heal: if the SSE stream drops (proxy timeout, sleep/resume),
      // a 5s poll of statusURL still advances the screen to completion.
      // Only while non-terminal — a finished run needs no polling (add mode:
      // only until the content landed or the add run failed).
      if (this.needsPolling()) {
        this._timer = setInterval(() => this.poll(), 5000);
      }
    },

    async poll() {
      try {
        // GET → no CSRF needed; same-origin carries the session cookie.
        const res = await fetch(this.statusURL, {
          headers: { "HX-Request": "true" },
          credentials: "same-origin",
        });
        if (res.ok) this.applySnapshot(await res.json());
      } catch {
        /* transient network error — the next tick retries */
      }
      if (this.editorURL !== "" && !this.landed) await this.checkLanding();
    },

    // retry POSTs to the wizard-scoped resume endpoint via window.htmx.ajax
    // (so htmx attaches CSRF). The handler responds 204 + HX-Location, which
    // htmx.ajax processes: it boosts (body swap) the WHOLE wizard page, so
    // the server re-renders it with the generation lock recomputed over the
    // new running run — freezing step nav + the assistant immediately — and
    // this component is re-hydrated fresh from that run's state. No manual
    // re-arm needed: the re-render replaces this component entirely (the old
    // NoContent path re-armed only this component, leaving nav/assistant
    // unlocked until the operator navigated).
    async retry() {
      // Guard against double-fire: a second click while the first retry is
      // still in flight must not supersede+recreate+enqueue a second run.
      // _retrying also drives the button's :disabled binding.
      if (this._retrying) return;
      this._retrying = true;
      try {
        // $el is Alpine's root-element magic (injected at runtime, not on
        // the inferred object type) — cast so strict tsc stays clean.
        await (window as any).htmx.ajax("POST", this.retryURL, { source: (this as any).$el });
      } finally {
        this._retrying = false;
      }
    },

    stop() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    },

    // Alpine calls destroy() when the element is torn down (navigation,
    // swap). Clearing the timer here prevents a leaked interval polling a
    // gone element.
    destroy() {
      this.stop();
    },

    // ── Presentation helpers (server-rendered rows bind to these) ──
    // stAt / stProgress / grpDone / grpStatus / grpActiveLabel / unitLabel
    // are shared with the CMS/management build-status pill — see
    // build-progress-timeline.ts. Spread onto this object so `this`
    // inside them resolves to this Alpine component instance at call
    // time (ordinary method-call binding — see that file's design note).
    ...buildTimelineGetters(),

    // failedStageLabel finds the first LIVE 'failed' stage in `stages` and
    // returns its precomputed display label from `stageLabels`. This is
    // what makes the failure branch's failed-stage line REACTIVE: the
    // server-rendered failedStage(run) in the .templ only reflects the run
    // as of initial page load, so a live running→failed SSE flip (the path
    // the wizard user is actually watching) would otherwise show no failed
    // stage at all. Returns '' when no stage is (yet) failed. Wizard-only:
    // the pill's popover has no per-stage failure line.
    failedStageLabel(): string {
      const i = this.stages.findIndex((s) => s.status === "failed");
      return i === -1 ? "" : this.stageLabels[i] || "";
    },
  };
}
